import {useState, useContext} from 'react';

import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Link,
  TextField,
  Typography,
  InputLabel,
  Stack,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';

import {LegalLinks} from '../LegalLinks';
import LinkedInSignup from '../LinkedInSignup/LinkedInSignup';

import CopyrightLink from '../../../../../components/CopyrightLink/CopyrightLink';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {EmpionTooltip} from '../../../../../components/EmpionTooltip/EmpionTooltip';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../../contexts/UserContext/UserContext';
import {greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';
import {AppConfig} from '../../../../../lib/AppConfig';
import {ApplicantRoleData} from '../../../../../types/User';
import {validatePasswordStrength, validateEmail, validatePhoneNumber} from '../../../../../utils/validators';
import {TalentService} from '../../../../talent/services/TalentService';
import {getTextFieldStyle} from '../../../../user/styles/getTextFieldStyle';
import {ApplicantOnboardingService} from '../../../onboarding/services/ApplicantOnboardingService';
import {SignUpService} from '../../services/SignUpService';
import {ApplicantSignUpFormValues} from '../../types';

interface Props {
  talentSurveyId: string;
  /** Called after sign-up succeeds, e.g. to clear survey session cookies. */
  onSignUpSuccess?: () => void | Promise<void>;
}

export default function SignUp({talentSurveyId, onSignUpSuccess}: Props) {
  const {t} = useTranslation();
  const lang = useContext(LanguageContext);
  const {userContextLogin} = useContext(UserContext);
  const navigate = useNavigate();
  const {dispatchMessage} = useContext(SnackBarContext);

  const [isSignUpInProgres, setSignUpInProgres] = useState(false);

  const validate = (values: ApplicantSignUpFormValues) => {
    const errors: {[key: string]: string} = {};
    if (values.firstName === '') {
      errors.firstName = t('registration.signUp.formLabels.errorMessages.firstName');
    }
    if (values.lastName === '') {
      errors.lastName = t('registration.signUp.formLabels.errorMessages.lastName');
    }
    if (!values.email) {
      errors.email = t('registration.signUp.formLabels.errorMessages.email');
    } else if (!validateEmail(values.email)) {
      errors.email = t('registration.signUp.formLabels.errorMessages.invalidEmail');
    }
    if (!values.mobilePhoneNumber) {
      errors.mobilePhoneNumber = t('registration.signUp.formLabels.errorMessages.noPhoneNumber');
    } else if (!validatePhoneNumber(values.mobilePhoneNumber)) {
      errors.mobilePhoneNumber = t('registration.signUp.formLabels.errorMessages.invalidPhoneNumber');
    }
    if (!values.password) {
      errors.password = t('registration.signUp.formLabels.errorMessages.password');
    } else if (!validatePasswordStrength(values.password)) {
      errors.password = t('registration.signUp.formLabels.errorMessages.invalidPassword');
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      mobilePhoneNumber: '',
      password: '',
      termsOfService: false,
    },
    validate,
    onSubmit: async (values) => {
      setSignUpInProgres(true);

      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        formData.append(key, String(value));
      });

      const signUpService = new SignUpService(formData, false, lang);

      signUpService.signUp().then(async (user) => {
        if (user) {
          // Clear survey session immediately (fire-and-forget) so it runs before any redirect
          Promise.resolve(onSignUpSuccess?.()).catch((err: unknown) =>
            console.error('Sign-up success callback failed:', err)
          );

          const roleData = user.role.data as ApplicantRoleData;

          TalentService.attributeTalentSurvey(talentSurveyId, roleData.applicantId);

          ApplicantOnboardingService.start(roleData.applicantId).then((applicantOnboarding) => {
            if (applicantOnboarding) {
              roleData.applicantOnboardingId = applicantOnboarding.data?.id;
              user.role.data = roleData;
            }
          });

          const message = t('registration.signUp.emailVerificationMessage');
          const severity = 'success';
          dispatchMessage(message, severity);
          userContextLogin(user);
          setSignUpInProgres(false);
          navigate(AppConfig.APPLICANT_ONBOARDING_PATH);
        } else {
          const message = t('registration.signUp.notPossibleToSignUp');
          const severity = 'error';
          dispatchMessage(message, severity);
          setSignUpInProgres(false);
        }
      });
    },
    validateOnMount: true,
  });

  const textFieldStyle = getTextFieldStyle();
  const inputProps = {
    sx: {
      borderColor: orangeColors[5],
      backgroundColor: orangeColors[7],
      color: greenColors[700],
    },
  };

  return (
    <Container component='main' maxWidth='sm'>
      <CssBaseline />
      <Box
        sx={{
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          padding: 3,
          borderRadius: 3,
        }}
      >
        <Typography variant='h4' color='primary' sx={{mb: 3}}>
          {t('registration.signUp.formLabels.talentTitle')}
        </Typography>

        {/* LinkedIn Signup Section */}
        <Box sx={{mb: 3}}>
          <EmpionTooltip
            title={t('registration.signUp.formLabels.acceptTermsFirst')}
            isEnabled={!formik.values.termsOfService}
          >
            <Box width={'100%'}>
              <LinkedInSignup isDisabled={!formik.values.termsOfService} talentSurveyId={talentSurveyId} />
            </Box>
          </EmpionTooltip>
        </Box>

        {/* Terms and Conditions for LinkedIn */}
        <Box sx={{mb: 1}}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formik.values.termsOfService}
                name='termsOfService'
                color='secondary'
                data-testid='terms-of-use-checkbox'
                sx={{
                  '& .MuiSvgIcon-root': {
                    color: greenColors[500],
                  },
                }}
              />
            }
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label={
              <Typography variant='caption' color='primary.dark'>
                {t('registration.signUp.formLabels.termsOfUseStatement')}
              </Typography>
            }
          />
          <Grid container justifyContent={'flex-start'}>
            <LegalLinks isApplicant />
          </Grid>
        </Box>

        {/* Divider */}
        <Box sx={{display: 'flex', alignItems: 'center', mb: 3}}>
          <Box sx={{flexGrow: 1, height: '1px', backgroundColor: greenColors[700]}} />
          <Typography variant='body2' color={greenColors[700]} sx={{mx: 2, fontWeight: 500}}>
            {t('shared.or')}
          </Typography>
          <Box sx={{flexGrow: 1, height: '1px', backgroundColor: greenColors[700]}} />
        </Box>

        <Box component='form' noValidate onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <InputLabel htmlFor='firstName'>
                <Typography variant='body1' color='primary'>
                  {t('registration.signUp.formLabels.firstName')}*
                </Typography>
              </InputLabel>
              <TextField
                required
                variant='outlined'
                autoComplete='given-name'
                name='firstName'
                inputProps={{'data-testid': 'create-account-first-name'}}
                fullWidth
                id='firstName'
                onChange={formik.handleChange}
                value={formik.values.firstName}
                error={!!formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                onBlur={formik.handleBlur}
                sx={textFieldStyle}
                InputProps={inputProps}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor='lastName'>
                <Typography variant='body1' color='primary'>
                  {t('registration.signUp.formLabels.lastName')}*
                </Typography>
              </InputLabel>
              <TextField
                required
                variant='outlined'
                fullWidth
                id='lastName'
                name='lastName'
                inputProps={{'data-testid': 'create-account-last-name'}}
                onChange={formik.handleChange}
                value={formik.values.lastName}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                onBlur={formik.handleBlur}
                autoComplete='family-name'
                sx={textFieldStyle}
                InputProps={inputProps}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor='email'>
                <Typography variant='body1' color='primary'>
                  {t('registration.signUp.formLabels.email')}*
                </Typography>
              </InputLabel>
              <TextField
                required
                variant='outlined'
                fullWidth
                id='email'
                name='email'
                inputProps={{'data-testid': 'create-account-email'}}
                onChange={formik.handleChange}
                value={formik.values.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                onBlur={formik.handleBlur}
                autoComplete='email'
                sx={textFieldStyle}
                InputProps={inputProps}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor='mobilePhoneNumber'>
                <Typography variant='body1' color='primary'>
                  {t('registration.signUp.formLabels.phoneNumber')}*
                </Typography>
              </InputLabel>
              <TextField
                required
                variant='outlined'
                fullWidth
                id='mobilePhoneNumber'
                name='mobilePhoneNumber'
                inputProps={{'data-testid': 'create-account-mobile-phone-number'}}
                onChange={formik.handleChange}
                value={formik.values.mobilePhoneNumber}
                error={formik.touched.mobilePhoneNumber && Boolean(formik.errors.mobilePhoneNumber)}
                helperText={formik.touched.mobilePhoneNumber && formik.errors.mobilePhoneNumber}
                onBlur={formik.handleBlur}
                autoComplete='mobilePhoneNumber'
                sx={textFieldStyle}
                InputProps={inputProps}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor='password'>
                <Typography variant='body1' color='primary'>
                  {t('registration.signUp.formLabels.password')}*
                </Typography>
              </InputLabel>
              <TextField
                required
                variant='outlined'
                fullWidth
                name='password'
                type='password'
                id='password'
                inputProps={{'data-testid': 'create-account-password'}}
                onChange={formik.handleChange}
                value={formik.values.password}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                onBlur={formik.handleBlur}
                autoComplete='new-password'
                sx={textFieldStyle}
                InputProps={inputProps}
              />
              <Typography variant='caption' color='primary.dark'>
                {t('registration.signUp.formLabels.passwordRequirements')}
              </Typography>
            </Grid>
          </Grid>
          <EmpionTooltip
            title={t('registration.signUp.formLabels.acceptTermsFirst')}
            isEnabled={!formik.values.termsOfService}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: 2,
                mb: 2,
                gap: 2,
              }}
            >
              <EmpionButton
                type='submit'
                disabled={!formik.isValid || !formik.values.termsOfService || formik.isSubmitting}
                fullWidth
                data-testid='to-cultural-profile-submit'
              >
                {t('registration.signUp.formLabels.signUpButton')}
              </EmpionButton>
            </Box>
          </EmpionTooltip>
          <Stack direction='column' alignItems='center'>
            <Link href={AppConfig.LOGIN_PATH} variant='body2' color='primary.dark' textAlign={{xs: 'center'}}>
              {t('registration.signUp.formLabels.alreadyHaveAnAccount')}
            </Link>

            <Link href={AppConfig.COMPANY_SIGN_UP_PATH} variant='body2' color='primary.dark' fontWeight={700}>
              {t('registration.signUp.formLabels.signupAsCompany')}
            </Link>
          </Stack>
        </Box>
      </Box>
      <CopyrightLink sx={{mt: 5}} />
      <Backdrop sx={{color: 'background.paper', zIndex: (theme) => theme.zIndex.drawer + 1}} open={isSignUpInProgres}>
        <CircularProgress color='secondary' />
      </Backdrop>
    </Container>
  );
}
